/**
 * @file 回答区子组件集合
 * 包含：Markdown 主内容、引用卡片、图表、文档、权限提示、错误提示等
 * 各子组件按服务端流式返回的 type 分发渲染
 */
import React, {
  useRef,
  useState,
  useCallback,
  forwardRef,
  useMemo,
  memo,
} from 'react';
import ReactMarkdown from 'react-markdown';
import SvgIcon from '@home/components/SvgIcon';
import ChartAdapter from '../MainContent/ChartAdapter';
import { Card, Icon, Modal } from 'tea-component';
import rehypeRaw from 'rehype-raw';
import gfm from 'remark-gfm';
import breaks from 'remark-breaks';
import classNames from 'classnames';
import Sources from '../../../svg/agent-sources.svg';
import Notice from '../../../svg/notice-yellow.svg';
import NoticeBlue from '../../../svg/notice-blue.svg';
import dataBrainDefault from '../../../svg/agent-default.svg';
import defaultTipsIcon from '@/pages/aigc/assets/svg/agent-link-tips.svg';
/** AI Gallery 卡片右侧下载图标 */
import aiGalleryDownloadIcon from '@/pages/aigc/assets/svg/agent-ds-export.svg';
import { useIntl } from 'umi';
import { useSetUrlSearchParam } from '@/pages/aigc/hooks/useQuery';
import EllipsisHover from '@gp/components/EllipsisHover';
import AigcEllipsisHover from '@/pages/aigc/components/EllipsisHover';
import CommonBubble from '@home/components/CommonBubble';
import './index.scss';
import {
  extractAndValidateJson,
  extractErrorMessage,
  parseJsonSafely,
} from '../../utils';
import { ChatContentType, TChartTemplate } from '@/pages/aigc/pages/Agent/ChatDSTemplate/type';
import { CHAT_DS_TEMPLATES } from '@/pages/aigc/pages/Agent/ChatDSTemplate/constants';
import { findLast, isEmpty } from 'lodash-es';
import { useDebounceEffect, useMemoizedFn } from 'ahooks';
import BasicImage from '@home/components/BasicImage';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import AgentEchart from '../AgentEchart';
import SignedImage from '../SignedImage';
import { ThinkCmp } from './SteamThinkCmp';
import { DataDescriptionCmp } from './DataDescriptionCmp';
import CostWarningCmp from './CostWarningCmp';
import { useDragChart } from '../../hooks/useDragChart';
import { useCosDownload } from '../../hooks/useCosDownload';
import { TableWrapper } from '../TableComponents';
import CodeWrapper from '../CodeWrapper';
import { DataChartRenderer } from './DataChartRenderer';
import { EchartsChartRenderer } from './EchartsChartRenderer';
import { AgentVegaChartAsync } from '../AgentVegaChart/async';
import KnowledgeCitation from '../KnowledgeCitation';
import SelectionAddKbTipGate from '../SelectionAddKbTip/components/SelectionAddKbTipGate';
import { isKbAddSelectionEnabled } from '../SelectionAddKbTip/utils';
import SignedLink from './SignedLink';
import { getFileFormatIcon } from '@/pages/aigc/pages/Agent/utils/fileFormatIconMap';
import { resolveCosFileExtension } from './_utils';
import { OpinionReportCardCmp } from './OpinionReportCardCmp';
import QuickInputBtnCmp from './QuickInputBtnCmp';
export { AgentVegaChartAsync };
export { TokenTotalCmp } from './TokenTotalCmp';
export { default as CostWarningCmp } from './CostWarningCmp';

interface EmbeddedSourceTipsProps {
  value?: {
    url: string;
    name: string;
  }[];
}

// reference区域
/** 数据来源条目属性 */
interface ReferenceItemProps {
  item: any;
  applyDefaultStyle: boolean;
  isCopilot: boolean;
}

/**
 * 每个模块独立于外层组件，单独抽离、上层组件已做n-1的组件层面缓存、这里在处理本轮对话中的上下文缓存
 * 数据来源引用条目（每个来源一个链接）
 * 支持自定义样式和 Copilot 模式
 */
// eslint-disable-next-line react/display-name
const ReferenceItem = forwardRef<HTMLAnchorElement, ReferenceItemProps>(
  ({ item, applyDefaultStyle, isCopilot }, ref) => {
    const { type, favicon, name } = item;
    const isDataBrainType = type === 'databrain';
    const imageUrl = item.image_url;
    const currentIcon = (isDataBrainType ? imageUrl || dataBrainDefault : favicon) || defaultTipsIcon;

    const isDefaultIcon = !imageUrl || !favicon;

    const getReferenceUrl = (item: any) => {
      if (item.type === 'databrain') {
        return item.url?.startsWith('/') ? item.url : `/${item.url}`;
      }
      return item.url;
    };

    return (
      <a
        className={classNames(
          'dba-reference-item',
          applyDefaultStyle && 'dba-reference-default',
        )}
        href={getReferenceUrl(item)}
        target="_blank"
        rel="noopener noreferrer"
        ref={ref}
        onClick={() => {
          buriedBtnPoint({
            pageId: '700501',
            buttonId: '700501012',
            buttonName: '数据来源',
            extInfo: JSON.stringify({
              message: item.name,
              url: item.url,
              from: isCopilot ? 'copilot' : 'agent',
            }),
            typeId: 'aigc',
          });
        }}
      >
        <span className='dba-reference-item-img-list'>
          <BasicImage
            src={currentIcon}
            className={classNames(
              'dba-reference-source-image',
              applyDefaultStyle && isDefaultIcon && !isDataBrainType && 'dba-icon-default',
            )}
            errorSrc={ type === 'databrain' ? dataBrainDefault : defaultTipsIcon}
          />
        </span>
        {applyDefaultStyle ? (
          <>
            <EllipsisHover text={name || '-'} />
            <SvgIcon type="aigc-share" />
          </>
        ) : (
          <div>
            <span className="dba-text-overflow">{name || '-'}</span>
            <SvgIcon type="aigc-share" />
          </div>
        )}
      </a>
    );
  },
);

ReferenceItem.displayName = 'ReferenceItem';

/**
 * 数据来源引用卡片
 * 支持自适应布局（最多展示 3 行，超出部分通过气泡展示）
 */
export const ReferenceCmp = (props: any) => {
  const { value, answer, from } = props;

  const { formatMessage } = useIntl();
  // 创建容器ref，用于获取DOM元素和尺寸
  const containerRef = useRef<HTMLDivElement>(null);
  const moreElRef = useRef<HTMLDivElement>(null);
  // 状态：显示的数据项
  const [displayedItems, setDisplayedItems] = useState<any[]>(value);
  // 状态：隐藏的数据项（超出部分）
  const [hiddenItems, setHiddenItems] = useState<any[]>([]);
  const isCopilot = from === 'copilot';

  // 布局计算函数，使用useCallback优化性能
  const calculateLayout = useCallback(() => {
    // 如果容器不存在或已计算过则返回
    if (!containerRef.current) return;
    // 使用requestAnimationFrame确保在浏览器重绘前计算
    requestAnimationFrame(() => {
      // 获取容器宽度
      const containerWidth: number = containerRef?.current?.offsetWidth || 0;
      // 获取所有子元素
      const children = containerRef.current ? Array.from(containerRef.current.children) as HTMLElement[] : [];
      // 初始化布局计算变量
      let currentLineWidth = 0; // 当前行累计宽度
      let visibleCount = 0;     // 可见项计数
      let line = 1;             // 当前行号
      // 遍历子元素计算布局
      for (let i = 0;i < children.length;i++) {
        const child = children[i];
        if (!child) continue;
        // 计算子元素宽度（包括8px间距）
        const childWidth = child.offsetWidth + 8;
        currentLineWidth += childWidth;
        // 如果是第三行，增加气泡框的预估宽度(44px)
        if (line === 3) {
          currentLineWidth += 44;
        }
        // 如果超出容器宽度，换行
        if (currentLineWidth > containerWidth) {
          line += 1;
          currentLineWidth = childWidth;
        }
        // 如果超过3行则停止
        if (line > 3) break;
        visibleCount = i + 1;
      }
      const num = isCopilot ? 3 : visibleCount;
      // 更新显示和隐藏的数据项
      setDisplayedItems(value.slice(0, num));
      setHiddenItems(value.slice(num));
      // 标记已完成计算
    });
  }, [value]);

  useDebounceEffect(
    () => {
      setDisplayedItems(value);
      calculateLayout();
    },
    [answer],
    {
      wait: 200,
    },
  );

  const renderItems = (dataList: any, applyDefaultStyle = true) =>
    dataList.map((item: any, index: number) => (
      <ReferenceItem
        key={index}
        item={item}
        applyDefaultStyle={applyDefaultStyle}
        isCopilot={isCopilot}
      />
    ));
  return (
    <Card className="dba-reference-card">
      <Card.Body>
        <div className="dba-reference-box">
          <div className="dba-reference-title">
            <img src={Sources} alt="Sources" />
            <span>{formatMessage({ id: 'dba_data_sources' })}</span>
          </div>
          <div className={
            classNames("dba-reference-content", {
              'dba-reference-content-copilot': isCopilot && hiddenItems.length > 0
            })
          } ref={containerRef}>
            {renderItems(displayedItems)}
            {hiddenItems.length > 0 && (
              <CommonBubble
                overlayClassName='dba-nav-menu-overlay'
                className="dba-reference-more-bubble-content"
                content={renderItems(hiddenItems, false)}
                closeOnScroll
                referenceElement={moreElRef.current || undefined}
              >
                <div ref={moreElRef} className="dba-reference-more">
                  +{hiddenItems.length}
                </div>
              </CommonBubble>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};




/**
 * 打开内嵌数据源 URL
 * 处理 v2/ 相对路径和 http 绝对路径
 */
export const handleOPenUrl = (item: { url: string; }) => {
  let {url} = item;
  if (url?.startsWith('v2')) {
    // 处理v2开头的相对路径
    url = `/${url}`;
  } else if (url?.startsWith('/v2')) {
    // 处理绝对路径的/v2开头
    url = `${url}`;
  } else if (url?.startsWith('http') || url?.startsWith('https')) {
    url = url;
  } 
  else {
    // 其他情况添加斜杠前缀
    url = url?.startsWith('/') ? url : `/${url}`;
  }
  window.open(url, '_blank',);
};

const EmbeddedSourceTipsCmp = memo(({value}: EmbeddedSourceTipsProps) => {
  // const ref = useRef(null);
  // const isHovering = useHover(ref);
  // const isSingular = value?.length === 1;
  const renderItem = useMemo(() => ()=>value?.map((task) =>{
    const isDatBrdinIcon = (task?.url?.startsWith('http') || task?.url?.startsWith('https'));
    return  (
      <div
        key={task.url}
        className="dba-reference-tips-more-list"
        onClick={() => handleOPenUrl(task)}
      >
        <div className="dba-reference-tips-more-img">
          <BasicImage
            src={ isDatBrdinIcon ? task?.favicon || defaultTipsIcon : task?.favicon || dataBrainDefault }
            errorSrc={
              isDatBrdinIcon ? defaultTipsIcon : dataBrainDefault
            }
          />
        </div>
        <div className="dba-reference-tips-more-text">
          {task.name}
          <SvgIcon type="aigc-share" />
        </div>
      </div>
    );
  }),[value]);
  // if (isSingular) {
  //   return (
  //     <>
  //       <span
  //         ref={ref}
  //         className="dba-reference-singular"
  //         onClick={() => handleOPenUrl(value?.[0])}
  //       >
  //         {value?.[0]?.name}
  //         {isHovering && <SvgIcon type="aigc-share"  style={{color: '#3C7BFF'}}/>}
  //       </span>
  //     </>
  //   );
  // }
  return (
    // TODO: 待吧这个bubble去掉，放在外层组件，tips只渲染一个bubble，通过移入移出事件+referenceElement字段，来控制定位，减少bubble的出现
    // 已实现，但是从目标元素再次移入bubble，不好控制....
    <CommonBubble
      closeOnScroll
      className="dba-reference-tips-more"
      content={renderItem()}
      overlayClassName='dba-nav-menu-overlay'
    >
      <span className="dba-reference-tips-more-num">{value?.length}</span>
    </CommonBubble>
  );
});
EmbeddedSourceTipsCmp.displayName = 'EmbeddedSourceTipsCmp';

/**
 * 主体 Markdown 内容区域
 * 渲染回答正文，支持自定义标签（bid/reference/cos 等）和划词加入知识库
 */
export const MainContentCmp = memo(
  (props: any) => {
    const {
      value,
      className,
      from,
      answer,
      chartHeight,
      isShowReference = true,
      messageId,
      frontend_metadata,
      isLastQA,
      streamingState,
      onRecommendClick,
    } = props;
    // 魔法海报场景：正文里后端会内嵌 ![](海报图) 大图，与"海报文件卡片"重复展示。
    // 注意：仅依据 frontend_metadata.type 判断（主对话框消息透传该字段；左侧内容规划编辑器不传，故不会误伤 MD 内容里的图片）。
    const isMagicPosterScene = frontend_metadata?.type === 'magic_poster';
    // 使用 ref 记录已解析的 data_id，避免流式输出时重复解析
    const parsedDataIdsRef = useRef<Set<string>>(new Set());
    const streamingStateRef = useRef(streamingState);
    const isLastQARef = useRef(isLastQA);
    const onRecommendClickRef = useRef(onRecommendClick);
    isLastQARef.current = isLastQA;
    streamingStateRef.current = streamingState;
    onRecommendClickRef.current = onRecommendClick;

    // 稳定组件配置
    const components = useMemo(
      () => ({
        databraindata: (restProps: any) => (
          <DataChartRenderer
            restProps={restProps}
            tagName="databraindata"
            answer={answer}
            from={from}
            chartHeight={chartHeight}
            parsedDataIdsRef={parsedDataIdsRef}
          />
        ),
        dbd: (restProps: any) => (
          <DataChartRenderer
            restProps={restProps}
            tagName="dbd"
            answer={answer}
            from={from}
            chartHeight={chartHeight}
            parsedDataIdsRef={parsedDataIdsRef}
          />
        ),
        ech: (restProps: any) => (
          <EchartsChartRenderer
            restProps={restProps}
            tagName="ech"
            answer={answer}
            parsedDataIdsRef={parsedDataIdsRef}
          />
        ),
        btn: (restProps: any) => (
          <QuickInputBtnCmp
            isLastQA={isLastQARef.current}
            streamingState={streamingStateRef.current}
            onRecommendClick={onRecommendClickRef.current}
          >
            {restProps?.children}
          </QuickInputBtnCmp>
        ),
        cos: (restProps: any) => {
          const cosPayload = extractAndValidateJson(restProps?.children) || {};
          if (isEmpty(cosPayload)) {
            return null;
          }
          const cosFileExt = resolveCosFileExtension(
            cosPayload?.url || cosPayload?.cos_url || '',
            cosPayload?.name || cosPayload?.title || '',
          );
          const isExcelFile = cosFileExt === 'xlsx' || cosFileExt === 'xls';
          const normalizedCosDocument = {
            ...cosPayload,
            name: cosPayload?.name || cosPayload?.title || '',
            title: cosPayload?.title || cosPayload?.name || '',
            url: cosPayload?.url || cosPayload?.cos_url || '',
            cos_url: cosPayload?.cos_url || cosPayload?.url || '',
            id: cosPayload?.id || props?.messageId,
            document_id: cosPayload?.document_id || cosPayload?.id || props?.messageId,
            // 只给 cos 标签 xlsx：和自动打开一致。HTML 不要打这个标记，否则分享页会按表格预览、打不开。
            isCosTag: isExcelFile,
          };
          return (
            <DocumentCmp
              handleUpdateDsReport={props?.handleUpdateDsReport}
              answer={props}
              messageId={props?.messageId}
              isLastQA={props?.isLastQA}
              streamingState={props?.streamingState}
              query={props?.query}
              frontend_metadata={props?.frontend_metadata}
              dsReport={props?.dsReport}
              value={normalizedCosDocument}
              subType={isExcelFile ? ChatContentType.SmartTable : ChatContentType.AiGallery}
            />
          );
        },
        tid: ({ children }: any) => 
          // 使用 span 标签替代自定义 tid 标签，避免 TypeScript 类型错误
          // 通过 data-tag-type="tid" 标记供下载功能识别
          // 下载时通过 querySelector('[data-tag-type="tid"]') 查找元素
          (
            <span
              data-tag-type="tid"
              style={{
                display: 'none',           // 从布局中移除
              }}
            >
              {children}
            </span>
          ),
        a: (linkProps: any) => <SignedLink {...linkProps} />,
        // eslint-disable-next-line react/display-name
        code: (props) => <CodeWrapper {...props}/>,
        // eslint-disable-next-line react/display-name
        table: (props) => <TableWrapper {...props} {...{ from, messageId, answer }}/>,
        // eslint-disable-next-line react/display-name
        bid: memo(
          (restProps: any) => {
            const { formatMessage } = useIntl();
            // 1、判断是否是bid占位符，如果不是则提前返回null，符合规范
            // 2、解析children传来的字符串，获取bidata数据协议
            const bidataProtocol = extractAndValidateJson(restProps?.children);
            // 使用拖拽 Hook
            const { 
              isDragging, 
              isHovering, 
              chartRef, 
              dragHandlers, 
              hoverHandlers 
            } = useDragChart({
              dataId: bidataProtocol?.data_id,
              // 是否需要缩略图
              needThumbnail: from === 'ds-agent'
            });
            // 3、再次兜底,如果没有bidata协议数据，则返回null
            if (isEmpty(bidataProtocol)) return null;
            // 4、直接使用bidata协议数据，添加必要的属性
            const dataSource = {
              ...bidataProtocol,
              from: from || 'ds-agent',
              chartHeight,
              hideSource: true
            };
            // 5、确保数据结构完整
            if (!dataSource.data) {
              console.warn('bid: bidata protocol missing data field');
              return null;
            }
            // 6、为所见即所得编辑保存时恢复<bidi>原文，埋入原始文本标记，方便后续保存的时候，可以直接读取，防止解析成
            const originalBidText = `<bid>${JSON.stringify(bidataProtocol)}</bid>`;
            
            const deleteChart = async () => {
              const yes = await Modal.confirm({
                caption: formatMessage({ id: 'dba_chart_delete_title' }),
                message: undefined,
                className: 'dba-chart-delete-modal',
                description: `${formatMessage({ id: 'dba_chart_delete_message' })} ${bidataProtocol.data?.title}`,
                okText: formatMessage({ id: 'dba_confirm' }),
                cancelText: formatMessage({ id: 'dba_chart_delete_cancel' }),
              });
              if (yes && chartRef.current) {
                chartRef.current.remove();
              }
            };

            return (
              <>
                <div
                  ref={chartRef}
                  className={`dba-agent-bid ${isDragging ? 'dba-agent-bid-dragging' : ''} ${isHovering ? 'dba-agent-bid-drag-over' : ''}`}
                  draggable
                  {...dragHandlers}
                >
                  <span className="dba-agent-bid-text" style={{ display: 'none' }}>{originalBidText}</span>
                  {from === 'ds-agent' && <div
                    className="dba-bid-ops"
                  >
                    <CommonBubble placement="left" dark overlayClassName="dba-report-file-selector-overlay" content={formatMessage({ id: 'dba_chart_draggable' })}>
                      <span {...hoverHandlers}>
                        <SvgIcon type='drag-icon'/>
                      </span>
                    </CommonBubble>
                    <CommonBubble placement="left" dark overlayClassName="dba-report-file-selector-overlay" content={formatMessage({ id: 'dba_chart_delete' })}>
                      <span>
                        <SvgIcon type="delete-icon" onClick={() => {
                          deleteChart();
                        }} />
                      </span>
                    </CommonBubble>
                  </div>}
                  <ChartCmp value={[dataSource]} />
                </div>
              </>
            );
          },
          (prev: any, next: any) => {
            // 提取新旧props的bidata协议内容进行比较
            const getBidData = (props: any) =>
              JSON.stringify(extractAndValidateJson(props.children));
            const prevBidData = getBidData(prev);
            const nextBidData = getBidData(next);
            // 当bidata协议内容相同时，直接停止更新
            const shouldUpdate = prevBidData === nextBidData;
            return shouldUpdate;
          },
        ),
        // eslint-disable-next-line react/display-name
        reference: memo(
          (restProps: any) => {
            if (restProps?.node?.tagName !== 'reference' || !isShowReference) {
              return null;
            }
            // 获取原始字符串内容，不进行链接转换
            const rawContent = restProps?.children?.map?.((item: any) => {
              if (typeof item === 'string') {
                return item;
              }
              return item?.props?.children?.join?.('') || '';
            })?.join?.('') || '';

            const linksArr = rawContent.split('[SEP]')?.map(item=>parseJsonSafely(item));
            // console.log(linksArr, rawContent)
            return (
              <span className="dba-reference-datasource-tips">
                <EmbeddedSourceTipsCmp value={linksArr}/>
              </span>
            );
          },
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          (prev: any, next: any) => false,
        ),
        img: ({ src, alt, notSign = false, ...props }: any) => {
          // 魔法海报场景：正文内嵌大图与海报文件卡片重复，主对话框只保留文件卡片，故此处不渲染正文大图
          if (isMagicPosterScene) {
            return null;
          }
          if (notSign) {
            return <img src={src} alt={alt} {...props} referrerPolicy="no-referrer"/>;
          }
          return <SignedImage src={src} alt={alt} {...props} />;
        }
      }),
      [isMagicPosterScene],
    );
    return (
      <SelectionAddKbTipGate
        className={className}
        enabled={isKbAddSelectionEnabled(from) && isShowReference}
      >
        <ReactMarkdown
          className={`dba-mainContent markdown-body`}
          skipHtml={false}
          components={components as any}
          rehypePlugins={[rehypeRaw]}
          remarkPlugins={[gfm, breaks]}
        >
          {value}
        </ReactMarkdown>
      </SelectionAddKbTipGate>
    );
  },
  (prevProps, nextProps) =>
    prevProps.value === nextProps.value &&
    prevProps.streamingState === nextProps.streamingState &&
    prevProps.isLastQA === nextProps.isLastQA,

);
MainContentCmp.displayName = 'MainContentCmp';

/**
 * 权限提示区域
 * 根据权限类型展示不同文案（无权限/部分权限/管理权限等）
 */
export const AccessCmp = (props: any) => {
  const { value } = props;
  const { formatMessage, locale } = useIntl();

  const { accessContent, isSensitive } = useMemo(()=>{
    let content: React.ReactNode = null;
    let sensitive = true;
    
    // 检查是否有管理看板权限
    const hasMgmtAccess = value?.mgmt_retrieved_data === true;
    
    // 提取游戏列表
    const formatGames = (games: string[], isBold = false) => {
      const text = games.join('、');
      return isBold ? (
        <span style={{ fontWeight: '600' }}>{text}</span>
      ) : text;
    };
    
    // 根据不同类型生成不同文案
    switch (value.type) {
      case 'none':
        if (hasMgmtAccess) {
          // intelligence_1 或 dashboard_1 且有管理看板权限的特殊文案
          sensitive = true;
          content = (
            <span>
              {formatMessage({ id: 'dba_access_mgmt_intelligence_prefix' })}
              {formatGames([formatMessage({ id: 'dba_access_mgmt_dashboard' })], true)}
              {formatMessage({ id: 'dba_access_mgmt_intelligence_suffix' })}
              {formatMessage({ id: 'dba_access_mgmt_dashboard' })}
              {formatMessage({ id: 'dba_access_mgmt_intelligence_end' })}
            </span>
          );
        }
        break;
      
      case 'intelligence_1':
      case 'dashboard_1':
        if (hasMgmtAccess) {
          // intelligence_1 或 dashboard_1 且有管理看板权限的特殊文案
          sensitive = true;
          content = (
            <span>
              {formatMessage({ id: 'dba_access_mgmt_intelligence_prefix' })}
              {formatGames([formatMessage({ id: 'dba_access_mgmt_dashboard' })], true)}
              {formatMessage({ id: 'dba_access_mgmt_intelligence_suffix' })}
              {formatMessage({ id: 'dba_access_mgmt_dashboard' })}
              {formatMessage({ id: 'dba_access_mgmt_intelligence_end' })}
            </span>
          );
        } else {
          // dashboard_1 或 intelligence_1 无管理权限的逻辑
          sensitive = false;
          content = <span>{formatMessage({ id: 'dba_access_nodash' })}</span>;
        }
        break;

      case 'dashboard_2':
        sensitive = false;
        if (!value.black_games || value.black_games.length === 0) {
          // 当black_games为空时，使用dashboard_1的格式
          content = <span>{formatMessage({ id: 'dba_access_nodash' })}</span>;
        } else {
          // 正常dashboard_2的格式
          content = (
            <span>
              {formatMessage({ id: 'dba_access_dash2_left' })}
              {formatGames(value.black_games)}
              {formatMessage({ id: 'dba_access_dash2_right' })}
            </span>
          );
        }
        break;

      case 'dashboard_3':
        if (!value.black_games || value.black_games.length === 0) {
          // 当black_games为空时，使用dashboard_4的格式
          content = (
            <span>
              {hasMgmtAccess ? (
                <>
                  {formatMessage({ id: 'dba_access_dash3_left' })}
                  {formatGames([formatMessage({ id: 'dba_access_mgmt_dashboard' })], true)}
                  {formatMessage({ id: 'dba_access_mgmt_permission_comma' })}
                  {formatGames(value.white_games)}
                  {formatMessage({ id: 'dba_access_dash3_right' })}
                  {formatMessage({ id: 'dba_access_mgmt_dashboard' })}、
                  {formatGames(value.white_games)}
                  {formatMessage({ id: 'dba_access_dash3_end' })}
                </>
              ) : (
                <>
                  {formatMessage({ id: 'dba_access_dash3_left' })}
                  {formatGames(value.white_games)}
                  {formatMessage({ id: 'dba_access_dash3_right' })}
                  {formatGames(value.white_games)}
                  {formatMessage({ id: 'dba_access_dash3_end' })}
                </>
              )}
            </span>
          );
        } else {
          // 正常dashboard_3的格式
          content = (
            <span>
              {hasMgmtAccess ? (
                <>
                  {formatMessage({ id: 'dba_access_dash3_left' })}
                  {formatGames([formatMessage({ id: 'dba_access_mgmt_dashboard' })], true)}
                  {formatMessage({ id: 'dba_access_mgmt_permission_comma' })}
                  {formatGames(value.white_games)}
                  {formatMessage({ id: 'dba_access_dash3_mid' })}
                  {formatGames(value.black_games)}
                  {formatMessage({ id: 'dba_access_dash3_right' })}
                  {formatMessage({ id: 'dba_access_mgmt_dashboard' })}、
                  {formatGames(value.white_games)}
                  {formatMessage({ id: 'dba_access_dash3_end' })}
                </>
              ) : (
                <>
                  {formatMessage({ id: 'dba_access_dash3_left' })}
                  {formatGames(value.white_games)}
                  {formatMessage({ id: 'dba_access_dash3_mid' })}
                  {formatGames(value.black_games)}
                  {formatMessage({ id: 'dba_access_dash3_right' })}
                  {formatGames(value.white_games)}
                  {formatMessage({ id: 'dba_access_dash3_end' })}
                </>
              )}
            </span>
          );
        }
        break;

      case 'dashboard_4':
        content = (
          <span>
            {hasMgmtAccess ? (
              <>
                {formatMessage({ id: 'dba_access_dash3_left' })}
                {formatGames([formatMessage({ id: 'dba_access_mgmt_dashboard' })], true)}
                {formatMessage({ id: 'dba_access_mgmt_permission_and' })}
                {formatGames(value.white_games)}
                {formatMessage({ id: 'dba_access_dash3_right' })}
                {formatMessage({ id: 'dba_access_mgmt_dashboard' })}
                {formatMessage({ id: 'dba_access_mgmt_dashboard_and' })}
                {formatGames(value.white_games)}
                {formatMessage({ id: 'dba_access_dash3_end' })}
              </>
            ) : (
              <>
                {formatMessage({ id: 'dba_access_dash3_left' })}
                {formatGames(value.white_games)}
                {formatMessage({ id: 'dba_access_dash3_right' })}
                {formatGames(value.white_games)}
                {formatMessage({ id: 'dba_access_dash3_end' })}
              </>
            )}
          </span>
        );
        break;

      default:
        content = null;
    }
    return { accessContent: content, isSensitive: sensitive };
     
  },[locale, value]);

  if (!value || typeof value !== 'object' || !value.type) {
    return null;
  }
  return (
    <div
      className={classNames(
        'dba-access-container',
        !isSensitive && 'dba-access-blue',
      )}
    >
      {isSensitive && (
        <div className="dba-access-title">
          <img src={Notice} alt="" />
          <span>{formatMessage({ id: 'dba_access_title' })}</span>
        </div>
      )}
      <div className="dba-access-box">
        {!isSensitive && (
          <div className="dba-access-icon">
            <div className="dba-access-icon-box">
              <img src={NoticeBlue} alt="" />
            </div>
          </div>
        )}
        <span
          className={classNames(
            'dba-acess-text',
            isSensitive && 'dba-access-content',
          )}
        >
          {accessContent}
        </span>
      </div>
    </div>
  );
};

/**
 * 图表渲染组件
 * 将图表数据交给 ChartAdapter 渲染
 */
export const ChartCmp = (props: any) => {
  const { value } = props || {};
  return (
    // <div className="dba-chart-container">
    //   <div className="dba-chart-item">
    <ChartAdapter apiData={value} />
    //   </div>
    // </div>
  );
};

/**
 * 异常/错误提示组件
 * 支持错误码映射、停止生成提示、网络错误提示
 */
export const ErrorQuestionCmp = (props: {
  value?: string;
  errorTipsMessage?: (value: string) => string;
  streamingState?: string;
}) => {
  const { value, errorTipsMessage, streamingState, flag, answer } = props || {};
  const { formatMessage } = useIntl();
  // 先读取错误码对应的错误提示，如果没有则使用接口返回的错误提示，如果都没有则使用默认提示
  let errorText = errorTipsMessage?.(value?.code) || extractErrorMessage?.(value?.message);
  if (value === 'stop') {
    errorText = formatMessage({ id: 'dba_chart_stop' });
  } else if (value?.errorMsg && value?.code) {
    errorText = value?.errorMsg;
  } else if (!errorText) {
    errorText = formatMessage({ id: 'dba_chat_systeem_error' });
  }
  // 这个语句只会在断网超限时显示
  const isTextData = useMemo(() => {
    if (flag && answer) {
      return answer?.find(task => task.type === 'text');
    }
    return false;
  }, [answer, flag]);
  return (
    <div className={classNames("dba-chart-stop", {
      // 这里判断一下是否是string类型 value
      ['dba-chart-stop-over']: typeof value === 'string' && value === 'stop' && streamingState && streamingState !== 'complete',
      // TODO: stop的这个样式，暂时搞不定。。。目前存在间距问题，故加特殊cls
      ['dba-chart-network-error-over']: flag && flag === 'errorRefresh' && streamingState && streamingState !== 'complete',
      ['dba-chart-network-error-position-over']: flag && flag === 'errorRefresh' && isTextData && streamingState && streamingState !== 'complete',
    })}>
      <Icon type="error" /> {errorText as any}
    </div>
  );
};

/**
 * 文档卡片组件
 * 支持 document/magic_poster_file/ai_gallery_file 多种文档类型
 * 提供预览、下载、Copilot 模式禁用等功能
 */
export const DocumentCmp = memo((props: any) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { handleUpdateDsReport, answer, value, messageId, isLastQA, streamingState, subType, frontend_metadata, dsReport, query } = props || {};
  const { title, size, name } = value || {};
  // ========== Hook 调用统一放置在组件顶部前列 ==========
  const { formatMessage } = useIntl();
  const setSearchParams = useSetUrlSearchParam();
  const { downloadCosFile } = useCosDownload();

  // ========== 衍生变量 ==========
  // 直接使用 subType 作为组件类型，默认为 'document'
  const componentType = subType || 'document';
  const isDocument = componentType === 'document';
  const isMagicPoster = componentType === 'magic_poster_file';
  /** 后端 artifact type：ai_gallery_file，与魔法海报同级映射 */
  const isAiGalleryFile = componentType === 'ai_gallery_file';
  const isMagicPosterPng = isMagicPoster && value?.file_suffix === 'png_file';
  const cosUrl: string = value?.cos_url || value?.url || '';
  // Copilot 模式下不支持文档类卡片预览，禁止点击 + hover 提示
  // AI Gallery / 智能表格（ai_excel_file）卡片已支持 Copilot 模式预览（复用 AiGalleryPanel / ChatSmartTable），故排除在禁用范围外
  const isCopilotMode = answer?.from === 'copilot';
  const isExcelFile = componentType === ChatContentType.SmartTable;
  const isCopilotPreviewDisabled = isCopilotMode && !isAiGalleryFile && !isExcelFile;
  /** 基于文件名 / cos_url 后缀统一判定：扩展名 / 是否仅能下载（非 HTML 一律下载） */
  const fileExt = useMemo(
    () => resolveCosFileExtension(cosUrl, name || title),
    [cosUrl, name, title],
  );

  const fileIcon = useMemo(() => {
    if (isDocument) return getFileFormatIcon('md');
    if (isAiGalleryFile) return getFileFormatIcon(fileExt || 'html');
    if (isMagicPoster) {
      return getFileFormatIcon(isMagicPosterPng ? 'png' : 'md');
    }
    return getFileFormatIcon('xlsx');
  }, [
    isDocument,
    isAiGalleryFile,
    isMagicPoster,
    isMagicPosterPng,
    fileExt,
  ]);
  const downloadOnly = fileExt !== 'html';

  // ========== 事件处理 ==========
  const handleOpenDsReport = useMemoizedFn(() => {
    if (isAiGalleryFile) {
      // ZIP 或非 HTML 文件 → 走下载
      if (downloadOnly) {
        if (!cosUrl) return;
        downloadCosFile(cosUrl, name || title || '');
        return;
      }
    }
    // HTML：注入 splitView 参数（仅当当前 template 不是真正的布局模板时。
    // knowledge 等会话属性会占住 template 参数，但不能作为「已在分栏布局」的信号，
    // 需剔除后才注入，否则知识库会话中 cos 返回的 html 无法预览）
    const currentTemplate = new URLSearchParams(window.location.search).get('template');
    if (!CHAT_DS_TEMPLATES.includes(currentTemplate as TChartTemplate)) {
      setSearchParams({ template: 'splitView' });
    }
    handleUpdateDsReport?.({
      show: true,
      initialShow: true,
      ...props?.value,
      frontend_metadata,
      componentType,
      answer,
      messageId,
      query,
      reportId: messageId,
    });
  });

  const loading = useMemo(() => {
    const isDoc = findLast(answer?.answer, (item) => item.type === componentType);
    if (isLastQA && streamingState && streamingState !== 'complete' && !isEmpty(isDoc)) {
      return true;
    }
    return false;

  }, [answer?.answer, isLastQA, streamingState]);

  const handleClickDownload = useMemoizedFn((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isCopilotPreviewDisabled || loading || !cosUrl) return;
    void downloadCosFile(cosUrl, name || title || '');
  });

  // 根据类型获取显示的文件类型标签
  const getFileTypeLabel = () => {
    if (isAiGalleryFile) {
      if (fileExt) return fileExt.toUpperCase();
      return 'HTML';
    }
    if (isDocument) return 'MD';
    if (isMagicPoster) return isMagicPosterPng ? 'PNG' : 'MD';
    return 'Excel';
  };

  /**
   * 根据类型获取卡片主标题
   */
  const getDisplayTitle = () => {
    if (isAiGalleryFile) {
      return (name || title) || '-';
    }
    return (isDocument ? title : name) || '-';
  };

  /**
   * 副行「类型标签」后的第二段：
   * - ai_gallery_file 的 ZIP / 非 HTML 文件：显示「点击下载」提示；
   * - ai_gallery_file 的 HTML：显示 AI Gallery 固定提示；
   * - 其它（document / magic_poster_file）：有则展示 size，保持 release 行为。
   */
  const getSubtitleSecondPart = (): { text: string; className?: string; } | null => {
    if (isAiGalleryFile && downloadOnly) {
      return {
        text: formatMessage({ id: 'dba_zip_file_download_hint' }),
        className: 'dba-chart-document-size__hint',
      };
    }
    if (isAiGalleryFile) {
      return {
        text: formatMessage({ id: 'dba_ai_gallery_document_hint' }),
        className: 'dba-chart-document-size__hint',
      };
    }
    if (size) {
      return { text: size };
    }
    return null;
  };

  const subtitleSecondPart = getSubtitleSecondPart();

  return (
    <CommonBubble
      content={isCopilotPreviewDisabled ? formatMessage({ id: 'dba_document_copilot_disabled' }) : null}
      overlayClassName="dba-nav-menu-overlay"
    >
      <div
        className={classNames('dba-chart-document', {
          'dba-chart-document--no-ds-report': !dsReport?.show,
          'dba-chart-document--disabled': isCopilotPreviewDisabled,
        })}
        onClick={handleOpenDsReport}
      >
        <div>
          {loading ? <Icon type="loading" /> : <BasicImage
            src={fileIcon}
            errorSrc={getFileFormatIcon('')}
          />}
        </div>
        <div  className="dba-chart-document-file">
          <AigcEllipsisHover
            text={getDisplayTitle()}
            maxLines={2}
          />
          <div
            className={classNames('dba-chart-document-size', {
              'dba-chart-document-size--gallery': isAiGalleryFile,
            })}
          >
            <span>{getFileTypeLabel()}</span>
            {subtitleSecondPart ? (
              <>
                {' · '}
                <span className={subtitleSecondPart.className}>
                  {subtitleSecondPart.text}
                </span>
              </>
            ) : null}
          </div>
        </div>
        {isAiGalleryFile ? (
          <img
            src={aiGalleryDownloadIcon}
            alt=""
            aria-hidden
            className={'dba-chart-document__download-icon'}
            onClick={handleClickDownload}
          />
        ) : (
          <SvgIcon type="right-arrow-icon" />
        )}
      </div>
    </CommonBubble>
  );
}, (prevProps, nextProps) => prevProps.messageId === nextProps.messageId);
DocumentCmp.displayName = 'DocumentCmp';

/**
 * 顺序渲染子组件
 * 根据服务端流式返回的 type 按顺序加载渲染对应的子组件
 */
export const SortCompChild = (props: any) => {
  const { answer } = props || {};
  const mapKey = {
    think: <ThinkCmp />,
    echartsOption: <AgentEchart />,
    text: <MainContentCmp />,
    reference: <ReferenceCmp />,
    error: <ErrorQuestionCmp />,
    sensitiveInfo: <AccessCmp />,
    costWarning: <CostWarningCmp />,
    document: <DocumentCmp />,
    data_description: <DataDescriptionCmp />,
    complete_summary: <DataDescriptionCmp />,
    bq_chart: <AgentVegaChartAsync />,
    opinion_report_card: <OpinionReportCardCmp />,
    database_query: <KnowledgeCitation />,
  };

  return (
    <div>
      {answer?.map((item: any, index: number) => {
        const comp = mapKey?.[item.type as keyof typeof mapKey];
        if (!comp) {
          return null;
        }
        // TODO: 样式调整待定
        return (
          <React.Fragment key={index}>
            <comp.type key={index} {...{ ...props, ...item, index }} />
          </React.Fragment>
        );
      })}
    </div>
  );
};
